import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@/lib/zodcompat'
import { toast } from 'sonner'
import { ArrowLeft } from 'lucide-react'
import { employeeSchema, type EmployeeFormData } from '@/validators/employee'
import { useEmployee, useCreateEmployee, useUpdateEmployee } from '@/hooks/useEmployees'
import { FormSection } from '@/components/form/form-section'
import { MoneyInput } from '@/components/form/money-input'
import { CpfInput } from '@/components/form/cpf-input'
import { PhoneInput } from '@/components/form/phone-input'
import { BankFields } from '@/components/form/bank-fields'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/ui/loading-button'
import { EMPLOYEE_ROLE_LABELS, type EmployeeRole } from '@/types/employee'
import { cn } from '@/lib/utils'

const selectClass = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
)

function toDateInput(iso: string | null | undefined) {
  if (!iso) return ''
  return new Date(iso).toISOString().slice(0, 10)
}

function toISOString(dateInput: string) {
  return new Date(dateInput).toISOString()
}

function apiError(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? fallback
  )
}

export function EmployeeFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id

  const { data: employee, isLoading: loadingEmployee } = useEmployee(id ?? '')
  const createMutation = useCreateEmployee()
  const updateMutation = useUpdateEmployee(id ?? '')
  const mutation = isEdit ? updateMutation : createMutation

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      name: '',
      cpf: '',
      role: 'OPERATIONAL',
      hireDate: '',
      baseSalary: '' as unknown as number,
      paymentDay: 5 as unknown as number,
      email: '',
      phone: '',
      bankName: '',
      bankAgency: '',
      bankAccount: '',
      bankAccountType: '',
      pixKey: '',
    },
  })

  useEffect(() => {
    if (employee) {
      reset({
        name: employee.name,
        cpf: employee.cpf,
        role: employee.role,
        hireDate: toDateInput(employee.hireDate),
        baseSalary: Number.parseFloat(employee.baseSalary) as unknown as number,
        paymentDay: employee.paymentDay,
        email: employee.email ?? '',
        phone: employee.phone ?? '',
        bankName: employee.bankName ?? '',
        bankAgency: employee.bankAgency ?? '',
        bankAccount: employee.bankAccount ?? '',
        bankAccountType: (employee.bankAccountType ?? '') as EmployeeFormData['bankAccountType'],
        pixKey: employee.pixKey ?? '',
      })
    }
  }, [employee, reset])

  async function onSubmit(data: EmployeeFormData) {
    try {
      const payload = { ...data, hireDate: toISOString(data.hireDate) }
      await mutation.mutateAsync(payload as EmployeeFormData)
      toast.success(isEdit ? 'Funcionário atualizado!' : 'Funcionário cadastrado!')
      navigate(isEdit ? `/employees/${id}` : '/employees')
    } catch (err) {
      toast.error(apiError(err, 'Erro ao salvar funcionário'))
    }
  }

  if (isEdit && loadingEmployee) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon-sm" onClick={() => navigate('/employees')}>
          <ArrowLeft size={16} />
        </Button>
        <div>
          <h1 className="text-xl font-semibold text-foreground">
            {isEdit ? 'Editar funcionário' : 'Novo funcionário'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit ? employee?.name : 'Cadastre um membro da equipe'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <FormSection title="Dados pessoais">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="name">Nome *</Label>
              <Input id="name" aria-invalid={!!errors.name} {...register('name')} />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>
            <CpfInput control={control} name="cpf" disabled={isEdit} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="role">Cargo *</Label>
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <select id="role" value={field.value} onChange={field.onChange} className={selectClass}>
                    {(Object.keys(EMPLOYEE_ROLE_LABELS) as EmployeeRole[]).map((r) => (
                      <option key={r} value={r}>
                        {EMPLOYEE_ROLE_LABELS[r]}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="hireDate">Admissão *</Label>
              <Input
                id="hireDate"
                type="date"
                aria-invalid={!!errors.hireDate}
                {...register('hireDate')}
              />
              {errors.hireDate && (
                <p className="text-xs text-destructive">{errors.hireDate.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" {...register('email')} />
              {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
            </div>
            <PhoneInput control={control} name="phone" />
          </div>
        </FormSection>

        <FormSection title="Remuneração">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="baseSalary">Salário base *</Label>
              <MoneyInput
                id="baseSalary"
                error={!!errors.baseSalary}
                {...register('baseSalary')}
              />
              {errors.baseSalary && (
                <p className="text-xs text-destructive">{errors.baseSalary.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Valor referencial — usado para pré-preencher o registro de pagamento.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="paymentDay">Dia de pagamento *</Label>
              <Input
                id="paymentDay"
                type="number"
                min={1}
                max={31}
                aria-invalid={!!errors.paymentDay}
                {...register('paymentDay')}
              />
              {errors.paymentDay && (
                <p className="text-xs text-destructive">{errors.paymentDay.message}</p>
              )}
            </div>
          </div>
        </FormSection>

        <BankFields
          register={register}
          errors={errors}
          bankNameName="bankName"
          bankAgencyName="bankAgency"
          bankAccountName="bankAccount"
          bankAccountTypeName="bankAccountType"
          pixKeyName="pixKey"
        />

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/employees')}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <LoadingButton type="submit" loading={mutation.isPending} loadingText="Salvando…">
            {isEdit ? 'Salvar alterações' : 'Cadastrar funcionário'}
          </LoadingButton>
        </div>
      </form>
    </div>
  )
}
